import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useToast } from "@/app/providers/ToastProvider";
import {
  checkInLibrary,
  fetchMediaDetail,
  findSubscribeForMedia,
  subscribeMedia,
  type MediaDetail,
} from "@/features/media/api";
import type { Subscribe } from "@/features/subscribe/api";
import { mediaIdOf } from "@/shared/lib/format";
import { proxyImageUrl } from "@/shared/lib/image";
import { Button } from "@/shared/ui/Button";
import { PageHeader } from "@/shared/ui/PageHeader";
import { Spinner } from "@/shared/ui/Spinner";

const TYPE_OK = new Set(["电影", "电视剧"]);
const MEDIA_ID_RE = /^(tmdb|douban|bangumi):[\w.-]+$/;

/** 媒体详情：订阅状态 / 是否在库 / 搜资源 */
export function MediaDetailPage() {
  const { mediaid: rawId } = useParams();
  const [params] = useSearchParams();
  const { toast } = useToast();

  const mediaId = rawId ? decodeURIComponent(rawId) : "";
  const typeName = params.get("type") || params.get("type_name") || "电影";
  const titleQ = params.get("title") || undefined;
  const yearQ = params.get("year") || undefined;

  const [media, setMedia] = useState<MediaDetail | null>(null);
  const [sub, setSub] = useState<Subscribe | null>(null);
  const [inLibrary, setInLibrary] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!mediaId || !TYPE_OK.has(typeName) || !MEDIA_ID_RE.test(mediaId)) {
      setError("无效的媒体参数");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const detail = await fetchMediaDetail(mediaId, typeName, {
        title: titleQ,
        year: yearQ,
      });
      if (!detail?.title) {
        setError("未找到媒体信息");
        setMedia(null);
        return;
      }
      setMedia(detail);
      const [s, lib] = await Promise.all([
        findSubscribeForMedia(detail).catch(() => null),
        checkInLibrary(detail).catch(() => null),
      ]);
      setSub(s);
      setInLibrary(lib);
    } catch (e) {
      setError(e instanceof Error ? e.message : "加载失败");
    } finally {
      setLoading(false);
    }
  }, [mediaId, typeName, titleQ, yearQ]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onSubscribe() {
    if (!media) return;
    setBusy(true);
    try {
      await subscribeMedia(media);
      toast("订阅成功", "success");
      setSub(await findSubscribeForMedia(media));
    } catch (e) {
      toast(e instanceof Error ? e.message : "订阅失败", "error");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[13px] text-fg-muted">
        <Spinner /> 加载媒体…
      </div>
    );
  }

  if (error || !media) {
    return (
      <div>
        <PageHeader title="媒体详情" />
        <div className="rounded-[var(--radius-md)] border border-danger/30 bg-danger/10 px-4 py-3 text-[13px] text-danger">
          {error || "无数据"}
        </div>
      </div>
    );
  }

  const mid = mediaIdOf(media) || mediaId;

  return (
    <div>
      <PageHeader
        title={media.title || "媒体详情"}
        description={[
          media.year,
          media.type,
          media.vote_average ? `★ ${media.vote_average}` : null,
        ]
          .filter(Boolean)
          .join(" · ")}
        actions={
          <Link to="/search">
            <Button variant="ghost">返回搜索</Button>
          </Link>
        }
      />

      <div className="flex flex-col gap-5 sm:flex-row">
        <div className="h-[280px] w-[180px] shrink-0 overflow-hidden rounded-[var(--radius-md)] border border-border bg-bg-card">
          {proxyImageUrl(media.poster_path) ? (
            <img
              src={proxyImageUrl(media.poster_path)}
              alt=""
              className="h-full w-full object-cover"
              referrerPolicy="no-referrer"
            />
          ) : null}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap gap-2 text-[12px]">
            <Badge
              ok={Boolean(sub)}
              label={sub ? `已订阅` : "未订阅"}
            />
            <Badge
              ok={inLibrary === true}
              label={
                inLibrary === true
                  ? "媒体库已有"
                  : inLibrary === false
                    ? "媒体库未找到"
                    : "库状态未知"
              }
            />
          </div>
          {media.overview ? (
            <p className="mt-4 text-[13px] leading-relaxed text-fg-muted">
              {media.overview}
            </p>
          ) : null}
          <div className="mt-5 flex flex-wrap gap-2">
            {!sub ? (
              <Button disabled={busy} onClick={() => void onSubscribe()}>
                订阅
              </Button>
            ) : (
              <Link to="/subscribe">
                <Button variant="ghost">查看订阅</Button>
              </Link>
            )}
            <Link
              to={`/search?q=${encodeURIComponent(media.title || "")}&media=${encodeURIComponent(mid)}`}
            >
              <Button>搜种子</Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Badge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={
        ok
          ? "rounded bg-success/15 px-2 py-0.5 text-success"
          : "rounded bg-bg-hover px-2 py-0.5 text-fg-subtle"
      }
    >
      {label}
    </span>
  );
}
